/**
 * Chat Agent Scope Context (Agents Phase 4, piece 3 — design §3, the scope-indicator chip).
 *
 * Holds the current chat scope. Default `null` = the orchestrator (the main chat spins up
 * runs). You SCOPE TO A RUN — by clicking a run card or a notification — to steer or answer
 * it; the chat input then shows a "talking to ● {agent}" chip. The × (or run completion)
 * clears the scope back to the orchestrator.
 *
 * This piece is the VISUAL scoping only — the chip, the scope state, the module trigger, and
 * clear. Actual message routing (a scoped message to the run's conversation vs the
 * orchestrator) is deferred/live work and deliberately NOT wired here.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

/** The agent identity rendered in the scope chip (matches the agents `ScopeChip` reads). */
export interface ChatScopeAgent {
  name: string
  /** Per-agent identity color — shared by the status dot + chip border. */
  color: string
  /** Optional status so the chip's dot can render paused/active. */
  status?: string
}

export interface ChatAgentScope {
  agentId: string
  /** Set when scoping to a specific run; omitted when scoping to an agent/routine. */
  runId?: string
  agent: ChatScopeAgent
}

interface ChatAgentScopeContextValue {
  /** The scoped run, or `null` for the orchestrator (default). */
  scope: ChatAgentScope | null
  /** Scope the chat to a run (steer/answer it). */
  setScope: (scope: ChatAgentScope) => void
  /** Clear back to the orchestrator. */
  clearScope: () => void
}

const ChatAgentScopeContext = createContext<ChatAgentScopeContextValue | undefined>(undefined)

export function ChatAgentScopeProvider({ children }: { children: ReactNode }) {
  const [scope, setScopeState] = useState<ChatAgentScope | null>(null)

  const setScope = useCallback((next: ChatAgentScope) => {
    setScopeState(next)
  }, [])

  const clearScope = useCallback(() => {
    setScopeState(null)
  }, [])

  const value = useMemo<ChatAgentScopeContextValue>(
    () => ({ scope, setScope, clearScope }),
    [scope, setScope, clearScope],
  )

  return (
    <ChatAgentScopeContext.Provider value={value}>{children}</ChatAgentScopeContext.Provider>
  )
}

export function useChatAgentScope() {
  const context = useContext(ChatAgentScopeContext)
  if (!context) {
    throw new Error('useChatAgentScope must be used within a ChatAgentScopeProvider')
  }
  return context
}

/**
 * Non-throwing read for surfaces that may render outside the provider (e.g. the ChatBox
 * composer in isolated test/Storybook harnesses). Falls back to the orchestrator (null
 * scope) with no-op setters, so the scope chip simply renders nothing.
 */
export function useChatAgentScopeOptional(): ChatAgentScopeContextValue {
  const context = useContext(ChatAgentScopeContext)
  return context ?? { scope: null, setScope: () => {}, clearScope: () => {} }
}
